// src/App.tsx - UPDATED WITH INACTIVITY MONITOR

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { POSPage } from '@/features/pos/POSPage';

// ============================================================
// BUSINESS SETUP
// ============================================================
import { BusinessesPage } from '@/features/business/pages/BusinessPage';
import { BusinessDetailPage } from '@/features/business/pages/BusinessDetailPage';
import { BranchesPage } from '@/features/settings/pages/BranchesPage';
import { BranchDetailPage } from '@/features/branches/pages/BranchDetailPage';
import { BankAccountsPage } from '@/features/banking/pages/BankAccountsPage';
import { BankAccountDetailPage } from '@/features/banking/pages/BankAccountDetailPage';
import { ExpensesPage } from '@/features/expenses/pages/ExpensesPage';

// ============================================================
// STAFF MANAGEMENT
// ============================================================
import { StaffPage } from '@/features/staff/pages/StaffPage';
import { StaffDetailPage } from '@/features/staff/pages/StaffDetailPage';
import { StaffPerformancePage } from '@/features/staff/pages/StaffPerformancePage';

// ============================================================
// CATALOG
// ============================================================
import { ProductsPage } from '@/features/products/pages/ProductsPage';
import { ProductDetailPage } from '@/features/products/pages/ProductDetailPage';
import { ProductFormPage } from '@/features/products/pages/ProductFormPage';
import { BulkProductUploadPage } from '@/features/products/pages/BulkProductUploadPage';
import { BulkProductUploadErrorsPage } from '@/features/products/pages/BulkProductUploadErrorsPage';
import { CategoriesPage } from '@/features/categories/CategoriesPage';
import { CategoryDetailPage } from '@/features/categories/CategoryDetailPage';
import { BarcodePrintPage } from '@/features/products/pages/BarcodePrintPage';
import { ProductVariantsPage } from './features/products/pages/ProductVariantsPage';
import { ProductBarcodesPage } from './features/products/pages/ProductBarcodesPage';
import { ProductBatchesPage } from './features/products/pages/ProductBatchesPage';
import { ProductImagesPage } from './features/products/pages/ProductImagesPage';
import { ProductStockPage } from './features/products/pages/ProductStockPage';
import { ProductUomsPage } from './features/products/pages/ProductUomsPage';

// ============================================================
// INVENTORY & STOCK - CONSOLIDATED
// ============================================================
import { InventoryDashboardPage } from './features/inventory/pages/InventoryDashboardPage';
import { StockLevelsPage } from './features/inventory/pages/StockLevelsPage';
import { StockDetailPage } from './features/inventory/pages/StockDetailPage';
import { ReviewConfirmPage } from './features/inventory/pages/ReviewConfirmPage';
import { CountsListPage } from './features/inventory/pages/CountsListPage';
import { CountDetailPage } from './features/inventory/pages/CountDetailPage';
import { BulkCountPage } from './features/inventory/pages/BulkCountPage';
import { AdjustStockPage } from './features/inventory/pages/AdjustStockPage';
import { BulkAdjustPage } from './features/inventory/pages/BulkAdjustPage';
import { TransfersPage } from './features/inventory/pages/TransfersPage';
import { BulkTransferPage } from './features/inventory/pages/BulkTransferPage';
import { LedgerPage } from './features/inventory/pages/LedgerPage';
import { SettingsPage as InventorySettingsPage } from './features/inventory/pages/SettingsPage';
import { SetupPage } from './features/inventory/pages/SetupPage';

// ============================================================
// PROCUREMENT
// ============================================================
import { ProcurementPage } from '@/features/procurement/ProcurementPage';
import { CreatePOPage } from '@/features/procurement/CreatePOPage';
import { CreateGRNPage } from '@/features/procurement/CreateGRNPage';
import { CreateReturnPage } from '@/features/procurement/CreateReturnPage';
import { CreateRequisitionPage } from '@/features/procurement/CreateRequisitionPage';
import { VendorsPage } from '@/features/procurement/VendorsPage';
import { VendorDetailPage } from '@/features/procurement/VendorDetailPage';
import { RequisitionListPage } from '@/features/procurement/RequisitionListPage';
import { PurchaseOrderListPage } from '@/features/procurement/PurchaseOrderListPage';
import { GRNListPage } from '@/features/procurement/GRNListPage';
import { ReturnListPage } from '@/features/procurement/ReturnListPage';
import { PurchaseOrderDetailPage } from '@/features/procurement/PurchaseOrderDetailPage';
import { GRNDetailPage } from '@/features/procurement/GRNDetailPage';
import { RequisitionDetailPage } from '@/features/procurement/RequisitionDetailPage';
import { ReturnDetailPage } from '@/features/procurement/ReturnDetailPage';

// ============================================================
// SALES & CUSTOMERS
// ============================================================
import { SalesPage } from '@/features/sales/SalesPage';
import { SaleDetailPage } from '@/features/sales/SaleDetailPage';
import { CustomerDisplay } from './features/pos/CustomerDisplay';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { CustomerDetailPage } from '@/features/customers/CustomerDetailPage';
import { LoyaltyManagementPage } from '@/features/loyalty/pages/LoyaltyManagementPage';

// ============================================================
// FINANCE
// ============================================================
import { InvoicesPage } from '@/features/invoices/InvoicesPage';
import { CreateInvoicePage } from '@/features/invoices/CreateInvoicePage';

// ============================================================
// SECURITY
// ============================================================
import { ReceiptVerificationPage } from '@/features/security/ReceiptVerificationPage';

// ============================================================
// HAMPERS & CLEARANCE
// ============================================================
import { HampersPage } from '@/features/hampers/HampersPage';
import { HamperDetailPage } from '@/features/hampers/HamperDetailPage';
import { CreateHamperPage } from '@/features/hampers/CreateHamperPage';
import { ClearancePage } from '@/features/clearance/ClearancePage';

// ============================================================
// REPORTS
// ============================================================
import { ReportsPage } from '@/features/reports/ReportsPage';
import { SalesSummaryReportPage } from '@/features/reports/pages/SalesSummaryReportPage';
import { SalesByProductReportPage } from '@/features/reports/pages/SalesByProductReportPage';
import { SalesByCashierReportPage } from '@/features/reports/pages/SalesByCashierReportPage';
import { SalesByPaymentReportPage } from '@/features/reports/pages/SalesByPaymentReportPage';
import { VoidRefundReportPage } from '@/features/reports/pages/VoidRefundReportPage';
import { StockValuationReportPage } from '@/features/reports/pages/StockValuationReportPage';
import { StockMovementReportPage } from '@/features/reports/pages/StockMovementReportPage';
import { LowStockReportPage } from '@/features/reports/pages/LowStockReportPage';
import { StockAgingReportPage } from '@/features/reports/pages/StockAgingReportPage';
import { TopCustomersReportPage } from '@/features/reports/pages/TopCustomersReportPage';
import { CustomerPurchaseHistoryPage } from '@/features/reports/pages/CustomerPurchaseHistoryPage';
import { LoyaltySummaryPage } from '@/features/reports/pages/LoyaltySummaryPage';
import { CustomerDebtReportPage } from '@/features/reports/pages/CustomerDebtReportPage';
import { StaffPerformanceReportPage } from '@/features/reports/pages/StaffPerformanceReportPage';
import { ShiftSummaryReportPage } from '@/features/reports/pages/ShiftSummaryReportPage';
import { TopSellingProductsPage } from '@/features/reports/pages/TopSellingProductsPage';
import { SlowMovingProductsPage } from '@/features/reports/pages/SlowMovingProductsPage';
import { ProfitMarginReportPage } from '@/features/reports/pages/ProfitMarginReportPage';
import { TaxReportPage } from '@/features/reports/pages/TaxReportPage';
import { DiscountSummaryPage } from '@/features/reports/pages/DiscountSummaryPage';
import { DailyRevenuePage } from '@/features/reports/pages/DailyRevenuePage';
import { ProfitLossReportPage } from '@/features/reports/pages/ProfitLossReportPage';

// ============================================================
// OTHER
// ============================================================
import { TutorialsPage } from '@/features/tutorials/TutorialsPage';

// ============================================================
// SETTINGS
// ============================================================
import { SettingsPage } from '@/features/settings/SettingsPage';
import { OrganizationPage } from '@/features/settings/pages/OrganizationPage';
import { OrganizationProfilePage } from '@/features/settings/pages/OrganizationProfilePage';
import { RolesPage } from '@/features/settings/pages/RolesPage';
import { PermissionsPage } from '@/features/settings/pages/PermissionsPage';
import { TaxesPage } from '@/features/settings/pages/TaxesPage';
import { PaymentMethodsPage } from '@/features/settings/pages/PaymentMethodsPage';
import { CurrenciesPage } from '@/features/settings/pages/CurrenciesPage';
import { TerminalsPage } from '@/features/settings/pages/TerminalsPage';
import { TablesPage } from '@/features/settings/pages/TablesPage';
import { ReceiptTemplatesPage } from '@/features/settings/pages/ReceiptTemplatesPage';
import { NumberSequencesPage } from '@/features/settings/pages/NumberSequencesPage';
import { DiscountReasonsPage } from '@/features/settings/pages/DiscountReasonsPage';
import { TenantSettingsPage } from '@/features/settings/pages/TenantSettingsPage';
import { ProcurementSettingsPage } from '@/features/settings/pages/ProcurementSettingsPage';

// ============================================================
// BOM & PRODUCTION
// ============================================================
import { BomPage } from '@/features/bom/pages/BomPage';
import { BomRecipeForm } from '@/features/bom/pages/BomRecipeForm';
import { BomRecipeDetailPage } from '@/features/bom/pages/BomRecipeDetailPage';
import { BomPosPage } from '@/features/bom/pages/BomPosPage';
import { KitchenView } from '@/features/pos/components/KitchenView';
import { ProductionPage } from '@/features/bom/pages/ProductionPage';
import { ProductionFloorsPage } from '@/features/bom/pages/ProductionFloorsPage';
import { ProductionFloorFormPage } from '@/features/bom/pages/ProductionFloorFormPage';
import { ProductionFloorDetailPage } from '@/features/bom/pages/ProductionFloorDetailPage';

// ============================================================
// SUPER ADMIN
// ============================================================
import { TenantsPage } from '@/features/super-admin/pages/TenantsPage';
import { TenantDetailPage } from '@/features/super-admin/pages/TenantDetailPage';
import { SuperAdminDashboard } from '@/features/super-admin/pages/SuperAdminDashboard';
import { FeatureFlagsPage } from '@/features/super-admin/pages/FeatureFlagsPage';
import { SubscriptionsPage } from '@/features/super-admin/pages/SubscriptionsPage';

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReorderSuggestionsPage } from './features/inventory/pages/ReorderSuggestionsPage';
import { MovementAnalyticsPage } from './features/inventory/pages/MovementAnalyticsPage';
import { ValuationDashboardPage } from './features/inventory/pages/ValuationDashboardPage';
import { LocationManagementPage } from './features/inventory/pages/LocationManagement';
import { AuditLogsPage } from './features/super-admin/pages/AuditLogsPage';
import { AuditLogDetailPage } from './features/super-admin/pages/AuditLogDetailPage';
import { NotificationPreferencesPage } from './features/inventory/pages/NotificationPreferencesPage';
import { BatchManagementPage } from './features/inventory/pages/BatchManagementPage';
import { ABCClassificationPage } from './features/inventory/pages/ABCClassificationPage';
import { ErrorLogsPage } from './features/super-admin/pages/ErrorLogsPage';
import { ErrorLogDetailPage } from './features/super-admin/pages/ErrorLogDetailPage';
import { InactivityMonitor } from './components/ui/InactivityMonitor';
import { VerifySubscriptionPage } from './features/auth/VerifySubscriptionPage';
import { SubscriptionPackagesPage } from './features/subscription/pages/SubscriptionPackagesPage';

// ============================================================
// PROTECTED ROUTE
// ============================================================

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, fetchUser, user, tenant } = useAuthStore();
    const [hasAttemptedFetch, setHasAttemptedFetch] = React.useState(false);

    useEffect(() => {
        let isMounted = true;
        if (isAuthenticated && !user) {
            fetchUser().finally(() => {
                if (isMounted) setHasAttemptedFetch(true);
            });
        } else if (user) {
            setHasAttemptedFetch(true);
        }
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, user, fetchUser]);

    if (!isAuthenticated) return <Navigate to="/login" replace />;

    // Show crisp light-mode loader while resolving session
    if (!user && !hasAttemptedFetch) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
                <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-slate-600">Verifying session...</p>
            </div>
        );
    }

    // Token was invalid or revoked, redirect to login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';

    let jwtIsPlatformAdmin = false;
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            jwtIsPlatformAdmin = payload.isPlatformAdmin || false;
        }
    } catch { }

    const isPlatformAdmin =
        jwtIsPlatformAdmin ||
        (user as any)?.isPlatformAdmin ||
        user.email?.toLowerCase() === 'inyangpsalms@gmail.com' ||
        tenant?.slug === 'system' ||
        user.tenantId === SYSTEM_TENANT_ID ||
        user.tenantId === '49a95754-da93-49ff-91a5-cd12dc082d28' ||
        user.tenantId === '9ec96208-5114-4cec-94f2-7797260c9ba2' ||
        user.tenantId === 'de89f22b-497e-4f6e-b1d6-0b11e922eb72' ||
        tenant?.name?.toLowerCase().includes('system platform') ||
        tenant?.name?.toLowerCase().includes('etipos system') ||
        localStorage.getItem('tenantSlug') === 'system';

    // Platform admins bypass tenant subscription verification
    if (isPlatformAdmin) {
        return <>{children}</>;
    }

    // Subscription check: if tenant is not loaded or not subscribed, redirect to verification
    if (!tenant || tenant.isSubscribed === false) {
        const targetTenantId = tenant?.id || user.tenantId;
        const targetEmail = user.email || '';
        const query = targetTenantId ? `?tenantId=${targetTenantId}&email=${encodeURIComponent(targetEmail)}` : '';
        return <Navigate to={`/verify-subscription${query}`} replace />;
    }

    return <>{children}</>;
}

// ============================================================
// KITCHEN DISPLAY (Standalone — no sidebar)
// ============================================================

function KitchenDisplayPage() {
    const user = useAuthStore((s) => s.user);
    const branchId = useAuthStore((s) => s.selectedBranchId) || '';
    const navigate = useNavigate();

    if (!user) return <Navigate to="/login" replace />;
    return <KitchenView onBack={() => navigate('/dashboard')} backTitle="Back to Dashboard" tenantId={user.tenantId} branchId={branchId} />;
}

// ============================================================
// APP
// ============================================================

export default function App() {
    return (
        <>
            <InactivityMonitor />
            <Routes>
                {/* SUBSCRIPTION VERIFICATION & PACKAGES (Standalone) */}
                <Route path="/verify-subscription" element={<VerifySubscriptionPage />} />
                <Route path="/subscribe" element={<SubscriptionPackagesPage />} />

                {/* AUTH ROUTES */}
                <Route element={<AuthLayout />}>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                </Route>

                {/* KITCHEN DISPLAY (Standalone) */}
                <Route path="/kitchen" element={<ProtectedRoute><KitchenDisplayPage /></ProtectedRoute>} />

                {/* PROTECTED ROUTES WITH SIDEBAR */}
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>

                    {/* MAIN */}
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/pos" element={<POSPage />} />
                    <Route path="/pos/display" element={<CustomerDisplay />} />

                    {/* BUSINESS SETUP */}
                    <Route path="/businesses" element={<BusinessesPage />} />
                    <Route path="/businesses/:businessId" element={<BusinessDetailPage />} />
                    <Route path="/branches" element={<BranchesPage />} />
                    <Route path="/branches/:branchId" element={<BranchDetailPage />} />
                    <Route path="/bank-accounts" element={<BankAccountsPage />} />
                    <Route path="/bank-accounts/:accountId" element={<BankAccountDetailPage />} />
                    <Route path="/expenses" element={<ExpensesPage />} />

                    {/* STAFF MANAGEMENT */}
                    <Route path="/staff" element={<StaffPage />} />
                    <Route path="/staff/:staffId" element={<StaffDetailPage />} />
                    <Route path="/staff/performance" element={<StaffPerformancePage />} />

                    {/* CATALOG */}
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route path="/categories/:categoryId" element={<CategoryDetailPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/products/new" element={<ProductFormPage />} />
                    <Route path="/products/bulk-upload" element={<BulkProductUploadPage />} />
                    <Route path="/products/bulk-upload/errors" element={<BulkProductUploadErrorsPage />} />
                    <Route path="/products/:productId" element={<ProductDetailPage />} />
                    <Route path="/products/:productId/edit" element={<ProductFormPage />} />
                    <Route path="/products/:productId/variants" element={<ProductVariantsPage />} />
                    <Route path="/products/:productId/uoms" element={<ProductUomsPage />} />
                    <Route path="/products/:productId/images" element={<ProductImagesPage />} />
                    <Route path="/products/:productId/stock" element={<ProductStockPage />} />
                    <Route path="/products/:productId/batches" element={<ProductBatchesPage />} />
                    <Route path="/products/:productId/barcodes" element={<ProductBarcodesPage />} />
                    <Route path="/products/barcodes/print" element={<BarcodePrintPage />} />

                    {/* ============================================================ */}
                    {/* INVENTORY & STOCK - CONSOLIDATED                             */}
                    {/* ============================================================ */}
                    <Route path="/inventory" element={<InventoryDashboardPage />} />
                    <Route path="/inventory/levels" element={<StockLevelsPage />} />
                    <Route path="/inventory/levels/:productId" element={<StockDetailPage />} />
                    <Route path="/inventory/review" element={<ReviewConfirmPage />} />
                    <Route path="/inventory/counts" element={<CountsListPage />} />
                    <Route path="/inventory/counts/:countId" element={<CountDetailPage />} />
                    <Route path="/inventory/counts/bulk" element={<BulkCountPage />} />
                    <Route path="/inventory/adjust" element={<AdjustStockPage />} />
                    <Route path="/inventory/adjust/bulk" element={<BulkAdjustPage />} />
                    <Route path="/inventory/transfers" element={<TransfersPage />} />
                    <Route path="/inventory/transfers/bulk" element={<BulkTransferPage />} />
                    <Route path="/inventory/ledger" element={<LedgerPage />} />
                    <Route path="/inventory/setup" element={<SetupPage />} />
                    <Route path="/inventory/settings" element={<InventorySettingsPage />} />
                    <Route path="/inventory/reorder" element={<ReorderSuggestionsPage />} />
                    <Route path="/inventory/analytics" element={<MovementAnalyticsPage />} />
                    <Route path="/inventory/valuation" element={<ValuationDashboardPage />} />
                    <Route path="/inventory/locations" element={<LocationManagementPage />} />
                    <Route path="/inventory/notifications" element={<NotificationPreferencesPage />} />
                    <Route path="/inventory/batches" element={<BatchManagementPage />} />
                    <Route path="/inventory/classification" element={<ABCClassificationPage />} />

                    {/* Redirect old routes to new consolidated paths */}
                    <Route path="/stock" element={<Navigate to="/inventory/levels" replace />} />
                    <Route path="/stock/*" element={<Navigate to="/inventory/levels" replace />} />
                    <Route path="/transactions" element={<Navigate to="/inventory/ledger" replace />} />
                    <Route path="/transfers" element={<Navigate to="/inventory/transfers" replace />} />

                    {/* RECIPE CATALOG & BOM */}
                    <Route path="/recipes" element={<BomPage />} />
                    <Route path="/recipes/new" element={<BomRecipeForm />} />
                    <Route path="/recipes/:recipeId" element={<BomRecipeDetailPage />} />
                    <Route path="/recipes/:recipeId/edit" element={<BomRecipeForm />} />
                    <Route path="/bom" element={<BomPage />} />
                    <Route path="/bom/new" element={<BomRecipeForm />} />
                    <Route path="/bom/:recipeId" element={<BomRecipeDetailPage />} />
                    <Route path="/bom/:recipeId/edit" element={<BomRecipeForm />} />
                    <Route path="/bom-pos" element={<BomPosPage />} />
                    <Route path="/bom/production" element={<ProductionPage />} />
                    <Route path="/bom/production-floors" element={<ProductionFloorsPage />} />
                    <Route path="/bom/production-floors/new" element={<ProductionFloorFormPage />} />
                    <Route path="/bom/production-floors/:floorId" element={<ProductionFloorDetailPage />} />
                    <Route path="/bom/production-floors/:floorId/edit" element={<ProductionFloorFormPage />} />

                    {/* PROCUREMENT */}
                    <Route path="/procurement" element={<ProcurementPage />} />
                    <Route path="/procurement/requisitions" element={<RequisitionListPage />} />
                    <Route path="/procurement/requisitions/new" element={<CreateRequisitionPage />} />
                    <Route path="/procurement/requisitions/:reqId" element={<RequisitionDetailPage />} />
                    <Route path="/procurement/orders" element={<PurchaseOrderListPage />} />
                    <Route path="/procurement/orders/new" element={<CreatePOPage />} />
                    <Route path="/procurement/orders/:poId" element={<PurchaseOrderDetailPage />} />
                    <Route path="/procurement/grns" element={<GRNListPage />} />
                    <Route path="/procurement/grns/new" element={<CreateGRNPage />} />
                    <Route path="/procurement/grns/:grnId" element={<GRNDetailPage />} />
                    <Route path="/procurement/returns" element={<ReturnListPage />} />
                    <Route path="/procurement/returns/new" element={<CreateReturnPage />} />
                    <Route path="/procurement/returns/:returnId" element={<ReturnDetailPage />} />
                    <Route path="/vendors" element={<VendorsPage />} />
                    <Route path="/vendors/:vendorId" element={<VendorDetailPage />} />

                    {/* SALES & CUSTOMERS */}
                    <Route path="/sales" element={<SalesPage />} />
                    <Route path="/sales/:saleId" element={<SaleDetailPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
                    <Route path="/loyalty" element={<LoyaltyManagementPage />} />

                    {/* FINANCE */}
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/invoices/new" element={<CreateInvoicePage />} />

                    {/* SECURITY */}
                    <Route path="/security/verify" element={<ReceiptVerificationPage />} />

                    {/* HAMPERS & CLEARANCE */}
                    <Route path="/hampers" element={<HampersPage />} />
                    <Route path="/hampers/new" element={<CreateHamperPage />} />
                    <Route path="/hampers/:hamperId" element={<HamperDetailPage />} />
                    <Route path="/clearance" element={<ClearancePage />} />

                    {/* REPORTS */}
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/sales/summary" element={<SalesSummaryReportPage />} />
                    <Route path="/reports/sales/by-product" element={<SalesByProductReportPage />} />
                    <Route path="/reports/sales/by-category" element={<SalesSummaryReportPage />} />
                    <Route path="/reports/sales/by-cashier" element={<SalesByCashierReportPage />} />
                    <Route path="/reports/sales/by-payment" element={<SalesByPaymentReportPage />} />
                    <Route path="/reports/sales/void-refund" element={<VoidRefundReportPage />} />
                    <Route path="/reports/inventory/valuation" element={<StockValuationReportPage />} />
                    <Route path="/reports/inventory/movement" element={<StockMovementReportPage />} />
                    <Route path="/reports/inventory/low-stock" element={<LowStockReportPage />} />
                    <Route path="/reports/inventory/aging" element={<StockAgingReportPage />} />
                    <Route path="/reports/customers/top" element={<TopCustomersReportPage />} />
                    <Route path="/reports/customers/purchase-history" element={<CustomerPurchaseHistoryPage />} />
                    <Route path="/reports/customers/loyalty" element={<LoyaltySummaryPage />} />
                    <Route path="/reports/customers/debt" element={<CustomerDebtReportPage />} />
                    <Route path="/reports/staff/performance" element={<StaffPerformanceReportPage />} />
                    <Route path="/reports/staff/shifts" element={<ShiftSummaryReportPage />} />
                    <Route path="/reports/products/top-selling" element={<TopSellingProductsPage />} />
                    <Route path="/reports/products/slow-moving" element={<SlowMovingProductsPage />} />
                    <Route path="/reports/products/profit-margin" element={<ProfitMarginReportPage />} />
                    <Route path="/reports/finance/tax" element={<TaxReportPage />} />
                    <Route path="/reports/finance/discounts" element={<DiscountSummaryPage />} />
                    <Route path="/reports/finance/daily-revenue" element={<DailyRevenuePage />} />
                    <Route path="/reports/finance/p-and-l" element={<ProfitLossReportPage />} />

                    {/* OTHER */}
                    <Route path="/tutorials" element={<TutorialsPage />} />

                    {/* SETTINGS */}
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/organization" element={<OrganizationPage />} />
                    <Route path="/settings/organization/profile" element={<OrganizationProfilePage />} />
                    <Route path="/settings/organization/inventory" element={<InventorySettingsPage />} />
                    <Route path="/settings/organization/procurement" element={<ProcurementSettingsPage />} />
                    <Route path="/settings/roles" element={<RolesPage />} />
                    <Route path="/settings/permissions" element={<PermissionsPage />} />
                    <Route path="/settings/taxes" element={<TaxesPage />} />
                    <Route path="/settings/payment-methods" element={<PaymentMethodsPage />} />
                    <Route path="/settings/currencies" element={<CurrenciesPage />} />
                    <Route path="/settings/terminals" element={<TerminalsPage />} />
                    <Route path="/settings/tables" element={<TablesPage />} />
                    <Route path="/settings/receipt-templates" element={<ReceiptTemplatesPage />} />
                    <Route path="/settings/number-sequences" element={<NumberSequencesPage />} />
                    <Route path="/settings/discount-reasons" element={<DiscountReasonsPage />} />
                    <Route path="/settings/tenant" element={<TenantSettingsPage />} />

                    {/* SUPER ADMIN */}
                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                    <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
                    <Route path="/super-admin/tenants" element={<TenantsPage />} />
                    <Route path="/super-admin/tenants/:tenantId" element={<TenantDetailPage />} />
                    <Route path="/super-admin/subscriptions" element={<SubscriptionsPage />} />
                    <Route path="/super-admin/plans" element={<FeatureFlagsPage />} />
                    <Route path="/super-admin/audit-logs" element={<AuditLogsPage />} />
                    <Route path="/super-admin/audit-logs/:logId" element={<AuditLogDetailPage />} />
                    <Route path="/super-admin/error-logs" element={<ErrorLogsPage />} />
                    <Route path="/super-admin/error-logs/:logId" element={<ErrorLogDetailPage />} />

                </Route>

                {/* REDIRECTS */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </>
    );
}